import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { EllipsisVerticalIcon, GlobeAmericasIcon } from '@heroicons/react/20/solid';
import { Squares2X2Icon, TableCellsIcon } from '@heroicons/react/24/outline';
import Layout from '../Layout';
import CreateProject from './CreateProject'; // Adjust the import path if necessary

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ProjectList() {
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const navigate = useNavigate();
  const [hasUnmapped, setHasUnmapped] = useState(false);

  useEffect(() => {
    const fetchUnmappedCount = async () => {
      if (!user) return;

      const { data: all, error } = await supabase
        .from('measurements')
        .select('id');

      const { data: mapped, error: err2 } = await supabase
        .from('project_mapping')
        .select('measurement_id');

      if (error || err2) {
        console.error('Error checking unmapped measurements:', error || err2);
        return;
      }

      const mappedIds = new Set(mapped.map((m) => m.measurement_id));
      const unmappedCount = all.filter((m) => !mappedIds.has(m.id)).length;

      setHasUnmapped(unmappedCount > 0);
    };

    fetchUnmappedCount();
  }, [user]);

  useEffect(() => {
    // Update the tab title when component mounts
    document.title = "Projects | TransTech Connect";

    return () => {
      document.title = "TransTech Connect"; // Reset title when unmounting
    };
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;

      setLoading(true);

      try {
        // Retrieve organization_id for the user.
        const { data: orgMember, error: orgMemberError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgMemberError) {
          console.error('Error fetching organization membership:', orgMemberError);
          setLoading(false);
          return;
        }

        const organizationId = orgMember.organization_id;

        // Fetch projects for this organization.
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .eq('organization_id', organizationId);

        if (projectsError) {
          console.error('Error fetching projects:', projectsError);
          setLoading(false);
          return;
        }

        setProjects(projectsData);
      } catch (error) {
        console.error('Unexpected error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user]);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-medium text-gray-500">Your Company's Projects</h2>
          
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'card'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4 mr-1.5" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TableCellsIcon className="h-4 w-4 mr-1.5" />
              Table
            </button>
          </div>
        </div>
        
        {hasUnmapped && (
          <button
            onClick={() => navigate('/unmappedproject')}
            className="inline-flex items-center px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-md shadow-sm transition"
          >
            <GlobeAmericasIcon className="h-5 w-5 mr-2" aria-hidden="true" />
            View Unmapped Measurements
          </button>
        )}
      </div>

      <div className="mb-4">
        {viewMode === 'card' ? (
          // Card View
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {projects.map((project) => (
              <li
                key={project.id}
                className="col-span-1 flex rounded-md shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/project/${project.id}`)}
              >
                <div
                  className={classNames(
                    'bg-blue-600',
                    'flex w-16 shrink-0 items-center justify-center rounded-l-md text-sm font-medium text-white'
                  )}
                >
                  {project.name ? project.name.charAt(0).toUpperCase() : 'P'}
                </div>
                <div className="flex flex-1 items-center justify-between truncate rounded-r-md border border-gray-200 bg-white">
                  <div className="flex-1 truncate px-4 py-2 text-sm">
                    <span className="font-medium text-gray-900 hover:text-gray-600">
                      {project.name}
                    </span>
                    <p className="text-gray-500">
                      {project.description ? project.description : 'No description'}
                    </p>
                  </div>
                  <div className="shrink-0 pr-2">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full bg-transparent text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon aria-hidden="true" className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          // Table View
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[...projects]
                  .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                  .map((project) => (
                  <tr
                    key={project.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/project/${project.id}`)}
                  >
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {project.name ? project.name.charAt(0).toUpperCase() : 'P'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {project.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {project.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={classNames(
                          project.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800',
                          'inline-flex px-2 text-xs font-semibold rounded-full py-1'
                        )}
                      >
                        {project.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="sr-only">Open options</span>
                        <EllipsisVerticalIcon aria-hidden="true" className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {projects.length === 0 && !loading ? (
        <div className="text-center py-12">
          <div className="mx-auto h-20 w-20 rounded-full bg-yellow-100 flex items-center justify-center">
            <GlobeAmericasIcon className="h-10 w-10 text-yellow-600" aria-hidden="true" />
          </div>
          <h3 className="mt-6 text-lg font-medium text-gray-900">No projects yet</h3>
          <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
            If you have measurements, map them to a project. If not, get started by uploading measurement data or scanning a QR code with the mobile app.
          </p>
        </div>
      ) : null}

      {/* CreateProject Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900 opacity-50"
            onClick={() => setShowCreateModal(false)}
          ></div>
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 z-50">
            <CreateProject onClose={() => setShowCreateModal(false)} />
          </div>
        </div>
      )}
    </Layout>
  );
}